import {LONGHAND_TO_CSS_PROPERTY} from '@packages/systems/style/properties';

export type DeviceMediaType = 'screen' | 'print';
export type MediaQueryType = 'all' | DeviceMediaType;
export type MediaFeatureType = 'maxWidth' | 'minWidth';
export type Device = {
  width: number;
  height?: number;
  mediaType: DeviceMediaType;
};

export const MediaType: {
  All: 'all';
  Screen: 'screen';
  Print: 'print';
} = {
  All: 'all',
  Screen: 'screen',
  Print: 'print',
};

type MediaFeatureEvaluator = (arg1: Device, arg2: number) => boolean;
const MEDIA_FEATURE_EVALUATORS: Partial<
  Record<MediaFeatureType, MediaFeatureEvaluator>
> = {
  maxWidth(device: Device, value: number) {
    return device.width <= value;
  },
  minWidth(device: Device, value: number) {
    return device.width >= value;
  },
};

export class MediaFeature {
  feature: MediaFeatureType;
  value: number;
  evaluator: MediaFeatureEvaluator;

  static MaxWidth(value: number): MediaFeature {
    return new MediaFeature('maxWidth', value);
  }

  static MinWidth(value: number): MediaFeature {
    return new MediaFeature('minWidth', value);
  }

  constructor(feature: MediaFeatureType, value: number) {
    this.feature = feature;
    this.value = value;
    // @ts-expect-error - TS2322 - Type 'MediaFeatureEvaluator | undefined' is not assignable to type 'MediaFeatureEvaluator'.
    this.evaluator = MEDIA_FEATURE_EVALUATORS[feature];
  }

  evaluate(device: Device): boolean {
    return this.evaluator(device, this.value);
  }
}

// NOTE: Media Query Feature Support
// Currently, we only support a single media condition using the discrete media
// feature syntax wrapped in a pair of parens, ex: (media-feature: media-value).
export class MediaQuery {
  mediaType: MediaQueryType;
  expression: MediaFeature;

  constructor(mediaType: MediaQueryType, expression: MediaFeature) {
    this.mediaType = mediaType;
    this.expression = expression;
  }

  // A media query matches if:
  // - media type matches device (MediaQueryType.All always matches)
  // - evaluator returns true
  matches(device: Device): boolean {
    // Check if the media type is invalid.
    if (this.mediaType !== 'all' && this.mediaType !== device.mediaType) {
      return false;
    }
    // Check if the media features match.
    return this.expression.evaluate(device);
  }

  toCSS(): string {
    const expression = this.expression;
    let buf = '';
    if (this.mediaType !== 'all') {
      buf += `${this.mediaType} and `;
    }
    const feature = formatCss(expression.feature);
    buf += `(${feature}: ${expression.value}px)`;
    return buf;
  }
}

// This wraps the properties module css longhand lookup for type safety.
function formatCss(property: MediaFeatureType): string {
  const longhand = LONGHAND_TO_CSS_PROPERTY[property];
  if (longhand == null) {
    console.error('Unknown media feature property:', property);
  }
  return longhand || property;
}
